import React from 'react';
import { PublicCourse, PublicClassroom } from '@/types/course';
import { Registration, PaymentInfo } from '@/services/registrationApi';
import { formatPriceSimple } from '@/utils/courseFormat';
import { CheckCircleIcon, PhoneIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';

interface CompanyInvoiceGuidanceProps {
  registration: Registration;
  paymentInfo: PaymentInfo;
  course: PublicCourse;
  classroom: PublicClassroom;
  onClose: () => void;
}

const HOTLINE = '0983213076';

const CompanyInvoiceGuidance: React.FC<CompanyInvoiceGuidanceProps> = ({
  registration,
  paymentInfo,
  course,
  classroom,
  onClose
}) => {
  const deadline = paymentInfo.deadline
    ? new Date(paymentInfo.deadline).toLocaleString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '';

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-m2-secondary/15 rounded-full mb-4">
          <CheckCircleIcon className="h-8 w-8 text-m2-secondary" />
        </div>
        <h3 className="text-xl font-semibold text-m2-on-surface mb-2">Đăng ký thành công!</h3>
        <p className="text-m2-on-surface-variant">
          Mã đăng ký: <span className="font-mono font-medium text-m2-primary">{registration.code}</span>
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-6">
        <h4 className="font-medium text-amber-900 mb-2">Hóa đơn công ty & thanh toán</h4>
        <p className="text-amber-900 text-sm leading-relaxed mb-4">
          Vui lòng <strong>liên hệ {HOTLINE}</strong> để được <strong>hướng dẫn hoàn thiện thủ tục</strong> thanh toán và xuất hóa đơn.
          Ứng dụng không hiển thị mã QR chuyển khoản cho trường hợp này.
        </p>
        <a
          href={`tel:${HOTLINE.replace(/\s/g, '')}`}
          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 text-sm font-medium"
        >
          <PhoneIcon className="h-5 w-5" />
          Gọi {HOTLINE}
        </a>
      </div>

      {typeof paymentInfo.amount === 'number' && (
        <div className="bg-m2-surface-low rounded-lg p-4 mb-4 text-sm">
          <span className="text-m2-on-surface-variant">Học phí giữ chỗ:</span>{' '}
          <span className="font-semibold text-m2-primary">{formatPriceSimple(paymentInfo.amount)}</span>
        </div>
      )}

      {deadline && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-amber-900">
            <CalendarDaysIcon className="h-5 w-5 flex-shrink-0" />
            <div>
              <div className="font-semibold">Hạn hoàn tất thủ tục / thanh toán (giữ chỗ)</div>
              <div className="text-sm">{deadline}</div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 pt-6 border-t">
        <h4 className="font-medium text-m2-on-surface mb-3">Tóm tắt đăng ký</h4>
        <div className="bg-m2-surface-low rounded-lg p-4 text-sm space-y-2">
          <div>
            <span className="text-m2-on-surface-variant">Khóa học:</span>
            <span className="ml-2 font-medium">{course.name}</span>
          </div>
          <div>
            <span className="text-m2-on-surface-variant">Lớp học:</span>
            <span className="ml-2 font-medium">{classroom.name}</span>
          </div>
          <div>
            <span className="text-m2-on-surface-variant">Mã đăng ký:</span>
            <span className="ml-2 font-mono font-medium text-m2-primary">{registration.code}</span>
          </div>
        </div>
      </div>

      <div className="flex justify-center mt-6 pt-6 border-t">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-2 bg-m2-primary text-m2-on-primary rounded-md hover:bg-eh-forest-shadow"
        >
          Đóng
        </button>
      </div>
    </div>
  );
};

export default CompanyInvoiceGuidance;
